import { alertController, loadingController, modalController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, State } from '@stencil/core';
import { ProjectData } from 'common';
import { authFetch, AuthService, getAccessToken } from 'src/global/service/AuthService';

const countryStates = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
};

@Component({
  tag: 'create-project-modal'
})
export class CreateProjectModal {
  static async present(): Promise<string> {
    const modal = await modalController.create({
      component: 'create-project-modal',
      cssClass: 'concourse-modal modal-height-auto',
      backdropDismiss: false,
    });

    return new Promise(resolve => {
      modal.addEventListener('dismiss', () => {
        resolve(null);
        modal.dismiss();
      });
      modal.addEventListener('created', (e: CustomEvent) => {
        resolve(e.detail as string);
        modal.dismiss();
      });
      modal.present();
    });
  }

  @State() project: ProjectData = {
    name: null,
    job_number: null,
    client_contract_number: null,
    description: null,
    address: null,
    city: null,
    state_code: null,
    county: null,
    country_code: 'USA',
    zip: null,
  };
  @State() errorMessage: string;
  @Event() dismiss: EventEmitter;
  @Event() created: EventEmitter<string>;

  async createNewProject() {
    if (!this.project.name) {
      this.errorMessage = 'Project name is required';
      return;
    }

    if (!this.project.address) {
      this.errorMessage = 'Project address is required';
      return;
    }

    if (!this.project.city) {
      this.errorMessage = 'Project city is required';
      return;
    }

    if (!this.project.zip) {
      this.errorMessage = 'Project zip is required';
      return;
    }

    if (!this.project.state_code) {
      this.errorMessage = 'Please select a state';
      return;
    }

    const loadingModal = await loadingController.create({ message: 'Creating project...' });
    try {
      this.errorMessage = null;
      loadingModal.present();
      let response = await this.createProject(this.project);
      loadingModal.dismiss();
      // session has changed because new role added to user.
      AuthService.setSessionData(response.session);
      this.created.emit(response.projectId);
    } catch (error) {
      alertController.create({ message: error.message }).then(alert => alert.present());
    } finally {
      loadingModal.dismiss();
    }
  }

  async createProject(projectData: any): Promise<any> {
    const url = globalThis.ccLambdaBaseUrl + '/fn/projects/create';
    const response = await authFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${getAccessToken()}`,
      },
      body: JSON.stringify(projectData),
    });

    if (response.status === 401) {
      throw new Error((await response.json()).message);
    }

    if (response.status === 400) {
      throw new Error((await response.json()).message);
    }

    return await response.json();
  }

  render() {
    return (
      <Fragment>
        <modal-header modalTitle="Create a Project" onDismiss={() => this.dismiss.emit()} />
        <div class="modal-content">
          <ion-grid>
            <ion-row>
              <ion-col>
                <ion-input label="Name" label-placement="stacked" onIonChange={e => (this.project.name = e.target.value)}></ion-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-input label="Job Number (optional)" label-placement="stacked" onIonChange={e => (this.project.job_number = e.target.value)}></ion-input>
              </ion-col>
              <ion-col>
                <ion-input
                  label="Client Contract Number (optional)"
                  label-placement="stacked"
                  onIonChange={e => (this.project.client_contract_number = e.target.value)}
                ></ion-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-input label="Address" label-placement="stacked" onIonChange={e => (this.project.address = e.target.value)}></ion-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-input label="City" label-placement="stacked" onIonChange={e => (this.project.city = e.target.value)}></ion-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-input label="Zip" label-placement="stacked" onIonChange={e => (this.project.zip = e.target.value)}></ion-input>
              </ion-col>
              <ion-col>
                <select-input
                  inputProps={{
                    label: 'State',
                    labelPlacement: 'stacked',
                    placeholder: 'Select state',
                    value: (this.project.state_code && countryStates[this.project.state_code]) || '',
                  }}
                  selectProps={{
                    id: 'project-state',
                    interface: 'popover',
                    value: this.project.state_code ?? undefined,
                  }}
                  onSelectChange={event => {
                    this.project = { ...this.project, state_code: event.detail };
                  }}
                >
                  {Object.entries(countryStates).map(([code, name]) => {
                    return (
                      <ion-select-option key={code} value={code}>
                        {name}
                      </ion-select-option>
                    );
                  })}
                </select-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col>
                <ion-textarea
                  label-placement="stacked"
                  label="Description (optional)"
                  rows="5"
                  value={this.project.description}
                  onIonInput={e => (this.project.description = e.detail.value)}
                ></ion-textarea>
              </ion-col>
            </ion-row>
          </ion-grid>
        </div>
        <ion-footer>
          {this.errorMessage && <ion-text color="danger">{this.errorMessage}</ion-text>}
          <ion-toolbar>
            <ion-buttons slot="end">
              <ion-button shape="round" color="primary" fill="solid" onClick={() => this.createNewProject()}>
                Create
              </ion-button>
            </ion-buttons>
          </ion-toolbar>
        </ion-footer>
      </Fragment>
    );
  }
}
